import type { StripeError } from '@stripe/stripe-js';

/**
 * Maps a Stripe `StripeError` (from `elements.submit()` or `stripe.confirmSetup`)
 * to the in-dialog banner copy and its Datadog severity.
 *
 * `card_error` / `validation_error` are user-correctable: Stripe returns a clean,
 * user-facing `message` there ("Your card was declined."), surfaced verbatim, and
 * they log at `info`.
 *
 * Every other type (api/connection/auth/rate-limit/invalid-request/idempotency,
 * or an unrecognised type) is a system, config, or transport problem: fixed
 * generic copy is shown (the raw `message` can leak internals and is never
 * rendered) and it logs at `error`.
 *
 * Nothing is ever dropped — see docs/agents/observability.md (EE-706).
 */
export type StripeLogLevel = 'error' | 'info';

export type StripeErrorClassification = {
  heading: string;
  body: string;
  logLevel: StripeLogLevel;
};

const PAYMENT_NOT_SAVED_HEADING = 'Payment Method Not Saved';
const STRIPE_MESSAGE_FALLBACK_BODY = 'Please check your payment details and try again.';
// Shared with the dialog dead-end body (`ErrorBody`) so the generic copy can't drift.
export const GENERIC_HEADING = 'Something Went Wrong';
export const GENERIC_BODY =
  "We couldn't process your request right now. Please try again later or contact support.";

const USER_CORRECTABLE_TYPES = new Set<StripeError['type']>(['card_error', 'validation_error']);

export const classifyStripeError = (error: StripeError): StripeErrorClassification => {
  if (USER_CORRECTABLE_TYPES.has(error.type)) {
    const message = error.message?.trim();
    return {
      heading: PAYMENT_NOT_SAVED_HEADING,
      body: message ? message : STRIPE_MESSAGE_FALLBACK_BODY,
      logLevel: 'info',
    };
  }
  return {
    heading: GENERIC_HEADING,
    body: GENERIC_BODY,
    logLevel: 'error',
  };
};
